/**
 * Gate 1 — Review Queue, matching the provided UI exactly.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Chip, IconButton, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckIcon from "@mui/icons-material/Check";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import EditReviewTestCaseDrawer from "./EditReviewTestCaseDrawer";
import SubmitSetForApprovalView from "./SubmitSetForApprovalView";
import { expectedBehaviorFieldLabels } from "../../utils/testCaseExpectedLabels";
import { unwrapResponseText } from "../../utils/unwrapResponseText";

const FONT = "Lato, sans-serif";

const TEST_TYPE_LABEL = {
  nominal: "Functional",
  functional: "Functional",
  edge: "Edge",
  adversarial: "Adversarial",
  failure_mode: "Failure mode",
};

const TEST_TYPE_CHIP_SX = {
  height: 24,
  fontWeight: 600,
  fontSize: 12,
  fontFamily: FONT,
  borderRadius: "999px",
  bgcolor: "#F5F5F5",
  color: "#555555",
  "& .MuiChip-label": { px: 1 },
};

function testTypeLabel(testType) {
  const key = String(testType || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return TEST_TYPE_LABEL[key] || TEST_TYPE_LABEL[testType] || "Functional";
}

const FOOTER_SHADOW = "0px 1px 2px 0px rgba(0, 0, 0, 0.04)";
const SUBMIT_BG =
  "linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(0deg, #005BF0, #005BF0)";
const SUBMIT_BG_HOVER =
  "linear-gradient(180deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.08) 100%), linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(0deg, #005BF0, #005BF0)";

const QUEUE_COLORS = {
  reviewing: "#555555",
  notReviewed: "#C2410C",
  flagged: "#E4A11A",
  confirmed: "#12B76A",
  skipped: "#111827",
  remaining: "#005BF0",
  idActive: "#005BF0",
  idIdle: "#555555",
};

const NAV_BTN_SHEEN = "linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.04) 100%)";
const NAV_BTN_SHEEN_HOVER = "linear-gradient(180deg, rgba(0, 0, 0, 0.04) 0%, rgba(0, 0, 0, 0.08) 100%)";

const navBtnSx = {
  fontFamily: FONT,
  fontWeight: 700,
  fontSize: 13,
  lineHeight: "20px",
  textTransform: "none",
  height: 32,
  minHeight: 32,
  px: "12px",
  py: 0,
  borderRadius: "4px",
  border: "1px solid #BBBBBB",
  bgcolor: "#F5F5F5",
  backgroundColor: "#F5F5F5",
  backgroundImage: NAV_BTN_SHEEN,
  color: "#555555",
  boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.08)",
  "& .MuiButton-startIcon": { color: "#555555", marginRight: "4px", marginLeft: 0 },
  "& .MuiButton-endIcon": { color: "#555555", marginLeft: "4px", marginRight: 0 },
  "&:hover": {
    bgcolor: "#F5F5F5",
    backgroundColor: "#F5F5F5",
    backgroundImage: NAV_BTN_SHEEN_HOVER,
    border: "1px solid #BBBBBB",
    boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.08)",
  },
  "&.Mui-disabled": {
    bgcolor: "#F5F5F5",
    backgroundColor: "#F5F5F5",
    backgroundImage: "none",
    border: "1px solid #E6E6E6",
    color: "#BBBBBB",
    boxShadow: "none",
    "& .MuiButton-startIcon, & .MuiButton-endIcon": { color: "#BBBBBB" },
  },
};

const suspendBtnSx = {
  fontFamily: FONT,
  fontWeight: 700,
  fontSize: 13,
  lineHeight: "20px",
  textTransform: "none",
  height: 28,
  minHeight: 28,
  px: "12px",
  py: 0,
  borderRadius: "4px",
  border: "1px solid #005BF0",
  bgcolor: "#FFFFFF",
  color: "#005BF0",
  boxShadow: "none",
  "&:hover": { bgcolor: "#F0F6FF", border: "1px solid #005BF0", boxShadow: "none" },
  "&.Mui-disabled": {
    bgcolor: "#FFFFFF",
    border: "1px solid #BDD6FF",
    color: "#BDD6FF",
  },
};

const editBtnSx = {
  fontFamily: FONT,
  fontWeight: 700,
  fontSize: 13,
  lineHeight: "20px",
  textTransform: "none",
  height: 28,
  minHeight: 28,
  px: "12px",
  py: 0,
  borderRadius: "4px",
  border: "1px solid #D0D5DD",
  bgcolor: "#F5F5F5",
  color: "#344054",
  boxShadow: "none",
  "&:hover": { bgcolor: "#EDEDED", border: "1px solid #D0D5DD" },
};

const confirmBtnSx = {
  fontFamily: FONT,
  fontWeight: 700,
  fontSize: 13,
  lineHeight: "20px",
  textTransform: "none",
  height: 28,
  minHeight: 28,
  px: "12px",
  py: 0,
  borderRadius: "4px",
  border: "1px solid transparent",
  color: "#FFFFFF",
  bgcolor: "#005BF0",
  boxShadow: "none",
  "&:hover": { bgcolor: "#004ACC", boxShadow: "none" },
  "&.Mui-disabled": { bgcolor: "#BDD6FF", color: "#FFFFFF" },
};

function promptTexts(tc) {
  if (!Array.isArray(tc?.input)) return [];
  return tc.input
    .map((item) => {
      if (typeof item === "string") return item;
      if (item && typeof item === "object") return item.text || item.prompt || item.content || "";
      return "";
    })
    .filter((text) => text.trim().length > 0);
}

function quotedQuestion(text) {
  const inner = String(text)
    .replace(/^[\s"'“”‘’]+|[\s"'“”‘’]+$/g, "")
    .replace(/"([^"]*)"/g, "$1")
    .trim();
  return `"${inner}"`;
}

function queueStatusLabel(tc, isCurrent) {
  if (tc.status === "verified") return "Confirmed";
  if (tc.status === "flagged") return "Flagged";
  if (tc.status === "deactivated") return "Skipped";
  if (isCurrent) return "Reviewing";
  return "Not reviewed";
}

function queueStatusColor(tc, isCurrent) {
  if (tc.status === "verified") return QUEUE_COLORS.confirmed;
  if (tc.status === "flagged") return QUEUE_COLORS.flagged;
  if (tc.status === "deactivated") return QUEUE_COLORS.skipped;
  if (isCurrent) return QUEUE_COLORS.reviewing;
  return QUEUE_COLORS.notReviewed;
}

function StatRow({ label, value, color }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.35 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#555555", fontFamily: FONT }}>
        {label}:
      </Typography>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color, fontFamily: FONT }}>{value}</Typography>
    </Box>
  );
}

function FieldCaption({ children }) {
  return (
    <Typography
      sx={{
        fontSize: 12,
        fontWeight: 700,
        color: "#707070",
        fontFamily: FONT,
        mb: 0.75,
      }}
    >
      {children}
    </Typography>
  );
}

function TruthBox({ label, text, tone }) {
  const expected = tone === "expected";
  return (
    <Box
      sx={{
        bgcolor: "#FFFFFF",
        border: "1px solid #E6E6E6",
        borderLeft: `4px solid ${expected ? "#14B872" : "#DE350B"}`,
        borderRadius: "4px",
        px: { xs: 1.25, sm: 1.5 },
        py: { xs: 1, sm: 1.25 },
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: "0.04em",
          color: expected ? "#14B872" : "#DE350B",
          mb: 0.35,
          fontFamily: FONT,
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: 14, color: "#000000", fontFamily: FONT, lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word", overflowWrap: "anywhere" }}>
        {text || "—"}
      </Typography>
    </Box>
  );
}

function QueueClearPanel({ onSubmit, onReturn, busy }) {
  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        px: { xs: 2, sm: 4 },
        py: { xs: 4, md: 6 },
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          bgcolor: "#14B872",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 2,
        }}
      >
        <CheckIcon sx={{ color: "#FFFFFF", fontSize: 32 }} />
      </Box>
      <Typography sx={{ fontSize: { xs: 20, md: 22 }, fontWeight: 800, color: "#111827", fontFamily: FONT, mb: 0.75 }}>
        Queue is clear
      </Typography>
      <Typography
        sx={{
          fontSize: 14,
          color: "#667085",
          fontFamily: FONT,
          maxWidth: 360,
          lineHeight: 1.5,
          mb: 3,
        }}
      >
        Every test case has a disposition. The set is ready to submit for approval.
      </Typography>
      <Button
        disableElevation
        disabled={busy}
        onClick={onSubmit}
        sx={{
          fontFamily: FONT,
          fontWeight: 700,
          fontSize: 14,
          textTransform: "none",
          color: "#FFFFFF",
          background: SUBMIT_BG,
          bgcolor: "#005BF0",
          width: { xs: "100%", sm: "auto" },
          maxWidth: "100%",
          minWidth: { xs: 0, sm: 191 },
          height: 40,
          minHeight: 40,
          gap: "4px",
          borderRadius: "4px",
          px: "16px",
          py: "8px",
          boxShadow: "none",
          "&:hover": { background: SUBMIT_BG_HOVER, boxShadow: "none" },
          "&.Mui-disabled": { color: "#FFFFFF", opacity: 0.6 },
        }}
      >
        Submit Set for Approval
      </Button>
      <Button
        disableRipple
        onClick={onReturn}
        sx={{
          mt: 1.25,
          fontFamily: FONT,
          fontWeight: 700,
          fontSize: 14,
          textTransform: "none",
          color: "#005BF0",
          bgcolor: "transparent",
          "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
        }}
      >
        Return to test cases
      </Button>
    </Box>
  );
}

function needsReview(tc) {
  return tc?.status === "not_reviewed" || tc?.status === "flagged";
}

export default function ReviewQueueView({
  testCases = [],
  metrics = [],
  risks = [],
  onClose,
  onUpdateStatus,
  onSaveEdit,
  onSuspend,
  onSubmitForApproval,
  onGoToTestSuite,
  busy = false,
  isContentGenerator = false,
}) {
  const [queueIds] = useState(() => {
    const reviewable = testCases.filter(needsReview);
    return (reviewable.length ? reviewable : testCases).map((tc) => tc.id);
  });
  const [skippedIds, setSkippedIds] = useState(() => new Set());
  const [actedIds, setActedIds] = useState(() => new Set());
  const [index, setIndex] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const queueListRef = useRef(null);
  const activeItemRef = useRef(null);

  const lastByIdRef = useRef(new Map());

  const queue = useMemo(() => {
    const byId = new Map(testCases.map((tc) => [tc.id, tc]));
    const next = queueIds.map((id) => byId.get(id) || lastByIdRef.current.get(id)).filter(Boolean);
    lastByIdRef.current = new Map(next.map((tc) => [tc.id, tc]));
    return next;
  }, [testCases, queueIds]);

  useEffect(() => {
    if (!queue.length) {
      setIndex(0);
      return;
    }
    setIndex((prev) => Math.min(prev, queue.length - 1));
  }, [queue.length]);

  const current = queue[index] || null;
  const isFirst = index <= 0;
  const isLast = !queue.length || index >= queue.length - 1;
  const hasActedOnCurrent = Boolean(
    current &&
      (actedIds.has(current.id) || current.status === "verified" || current.status === "deactivated")
  );
  const previousDisabled = isFirst || busy;
  const nextDisabled = isLast || busy || !hasActedOnCurrent;

  useEffect(() => {
    activeItemRef.current?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [index]);

  const markActed = (id) => {
    if (!id) return;
    setActedIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    setSkippedIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const stats = useMemo(() => {
    let confirmed = 0;
    let flagged = 0;
    let skipped = 0;
    let remaining = 0;
    const byId = new Map(queue.map((tc) => [tc.id, tc]));
    queueIds.forEach((id) => {
      const tc = byId.get(id);
      if (!tc) remaining += 1;
      else if (tc.status === "verified") confirmed += 1;
      else if (tc.status === "deactivated" || skippedIds.has(tc.id)) skipped += 1;
      else if (tc.status === "flagged") flagged += 1;
      else remaining += 1;
    });
    return {
      confirmed,
      flagged,
      skipped,
      remaining,
      total: queueIds.length,
      completed: confirmed + flagged + skipped,
    };
  }, [queue, queueIds, skippedIds]);

  const queueClear = queue.length > 0 && stats.remaining === 0;

  useEffect(() => {
    if (queueClear) setEditOpen(false);
    else setSubmitOpen(false);
  }, [queueClear]);

  const handleSubmitForApproval = () => {
    setSubmitOpen(true);
  };

  const handleSendForSignOff = (note) => {
    onSubmitForApproval?.(note);
  };

  const goToNextPending = (actedId) => {
    setIndex((i) => {
      const nextAfter = queue.findIndex(
        (tc, idx) => idx > i && tc.id !== actedId && needsReview(tc)
      );
      if (nextAfter >= 0) return nextAfter;
      const anyOther = queue.findIndex((tc) => tc.id !== actedId && needsReview(tc));
      return anyOther >= 0 ? anyOther : i;
    });
  };

  const goNext = () => {
    if (nextDisabled || !current) return;
    setIndex((i) => i + 1);
  };

  const goPrev = () => {
    if (!previousDisabled) setIndex((i) => i - 1);
  };

  const handleConfirm = async () => {
    if (!current || busy) return;
    const ok = await onUpdateStatus(current, "verified");
    if (ok) {
      markActed(current.id);
      goToNextPending(current.id);
    }
  };

  const handleConfirmFromFlyout = async (payload) => {
    if (!current || busy) return;
    const ok = await onSaveEdit(current, payload);
    if (ok) {
      markActed(current.id);
      setEditOpen(false);
      goToNextPending(current.id);
    }
  };

  useEffect(() => {
    if (busy || queueClear || !current) return;
    if (current.status !== "deactivated") return;
    markActed(current.id);
    goToNextPending(current.id);
  }, [busy, queueClear, current?.id, current?.status]);

  if (submitOpen) {
    return (
      <SubmitSetForApprovalView
        testCases={queue}
        metrics={metrics}
        onBack={() => setSubmitOpen(false)}
        onCancel={() => setSubmitOpen(false)}
        onSend={handleSendForSignOff}
        onClose={onClose}
        onGoToTestSuite={onGoToTestSuite}
        busy={busy}
      />
    );
  }

  if (!queue.length) {
    return (
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, bgcolor: "#F5F5F5" }}>
        <ReviewQueueHeader onClose={onClose} />
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", p: 4 }}>
          <Box sx={{ textAlign: "center", maxWidth: 420 }}>
            <FactCheckOutlinedIcon sx={{ fontSize: 48, color: "#98A2B3", mb: 1 }} />
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#344054", mb: 0.5, fontFamily: FONT }}>
              No test cases in the review queue
            </Typography>
            <Typography sx={{ fontSize: 14, color: "#667085", mb: 2, fontFamily: FONT }}>
              Only cases that are new or flagged for review appear here.
            </Typography>
            <Button variant="outlined" onClick={onClose} sx={suspendBtnSx}>
              Back to test cases
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  const prompts = promptTexts(current);
  const typeLabel = testTypeLabel(current?.testType);

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, minWidth: 0, bgcolor: "#F5F5F5", overflow: "hidden" }}>
      <ReviewQueueHeader onClose={onClose} />

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: { xs: 1.25, md: 2 },
          p: { xs: 1.25, sm: 1.75, md: 2.5 },
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: { xs: "100%", md: 240, lg: 268 },
            flex: { xs: "0 0 auto", md: "0 0 auto" },
            flexShrink: 0,
            bgcolor: "#FFFFFF",
            border: "1px solid #E6E6E6",
            borderRadius: "8px",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            maxHeight: { xs: 200, sm: 240, md: "none" },
            overflow: "hidden",
          }}
        >
          <Box sx={{ px: 2, pt: 2, pb: 1.75, borderBottom: "1px solid #E6E6E6" }}>
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#111827", fontFamily: FONT, mb: 1 }}>
              Review Stats
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#555555", fontFamily: FONT, mb: 0.25 }}>
              Remaining: {stats.remaining}
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#555555", fontFamily: FONT, mb: 1.25 }}>
              Review Progress: {stats.completed} of {stats.total} completed
            </Typography>
            <StatRow label="Confirmed" value={stats.confirmed} color={QUEUE_COLORS.confirmed} />
            <StatRow label="Flagged" value={stats.flagged} color={QUEUE_COLORS.flagged} />
            <StatRow label="Skipped" value={stats.skipped} color={QUEUE_COLORS.skipped} />
            <Box sx={{ borderTop: "1px solid #E6E6E6", mt: 0.5, pt: 0.5 }}>
              <StatRow label="Remaining" value={stats.remaining} color={QUEUE_COLORS.remaining} />
            </Box>
          </Box>

          <Typography
            sx={{
              px: 2,
              pt: 1.5,
              pb: 0.75,
              fontSize: 13,
              fontWeight: 800,
              color: "#111827",
              fontFamily: FONT,
            }}
          >
            Upcoming in queue
          </Typography>
          <Box ref={queueListRef} sx={{ flex: 1, overflowY: "auto", pb: 1 }}>
            {queueClear ? (
              <Box
                sx={{
                  mx: 1.5,
                  px: 1.5,
                  py: 1,
                  borderRadius: "6px",
                  bgcolor: "#F5F5F5",
                  border: "1px solid #E6E6E6",
                }}
              >
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#98A2B3", fontFamily: FONT, textAlign: "center" }}>
                  No cases remaining
                </Typography>
              </Box>
            ) : (
              queue.map((tc, i) => {
                const active = i === index;
                const canSelect = i < index || hasActedOnCurrent;
                return (
                  <Box
                    key={tc.id}
                    ref={active ? activeItemRef : undefined}
                    onClick={() => {
                      if (i === index) return;
                      if (i < index || hasActedOnCurrent) setIndex(i);
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      px: 1.5,
                      py: 0.85,
                      mx: 1,
                      borderRadius: "6px",
                      cursor: canSelect || active ? "pointer" : "default",
                      bgcolor: active ? "#F5F5F5" : "transparent",
                      "&:hover": {
                        bgcolor: active ? "#F5F5F5" : canSelect ? "#FAFAFA" : "transparent",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: active ? QUEUE_COLORS.idActive : QUEUE_COLORS.idIdle,
                        fontFamily: FONT,
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {tc.name || "—"}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 600,
                        fontFamily: FONT,
                        color: queueStatusColor(tc, active),
                        flexShrink: 0,
                      }}
                    >
                      {queueStatusLabel(tc, active)}
                    </Typography>
                  </Box>
                );
              })
            )}
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: { xs: 280, md: 0 },
            bgcolor: "#FFFFFF",
            border: "1px solid #E6E6E6",
            borderRadius: "8px",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {queueClear ? (
            <QueueClearPanel onSubmit={handleSubmitForApproval} onReturn={onClose} busy={busy} />
          ) : (
            <>
          <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", px: { xs: 1.5, sm: 2, md: 3 }, py: { xs: 1.5, md: 2.5 } }}>
            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, mb: { xs: 1.5, md: 2.5 } }}>
              <Typography sx={{ fontSize: { xs: 16, sm: 18, md: 20 }, fontWeight: 800, color: "#111827", fontFamily: FONT }}>
                {current.name || "—"}
              </Typography>
              <Chip
                label={
                  current.status === "flagged"
                    ? "Flagged"
                    : current.status === "verified"
                    ? "Confirmed"
                    : current.status === "deactivated"
                    ? "Deactivated"
                    : "Not reviewed"
                }
                size="small"
                sx={{
                  height: 24,
                  fontWeight: 600,
                  fontSize: 12,
                  fontFamily: FONT,
                  borderRadius: "999px",
                  bgcolor:
                    current.status === "verified"
                      ? "#DCFCE7"
                      : current.status === "flagged"
                      ? "#FFEDD5"
                      : current.status === "deactivated"
                      ? "#F5F5F5"
                      : "#FEF3C7",
                  color:
                    current.status === "verified"
                      ? "#15803D"
                      : current.status === "flagged"
                      ? "#C2410C"
                      : current.status === "deactivated"
                      ? "#707070"
                      : "#B45309",
                  "& .MuiChip-label": { px: 1 },
                }}
              />
              <Chip label={typeLabel} size="small" variant="filled" sx={TEST_TYPE_CHIP_SX} />
            </Box>

            <Box sx={{ mb: 2.5 }}>
              <FieldCaption>Test Case Title</FieldCaption>
              <Typography
                sx={{
                  fontFamily: "Lato, sans-serif",
                  fontWeight: 400,
                  fontStyle: "normal",
                  fontSize: 16,
                  lineHeight: "24px",
                  letterSpacing: 0,
                  color: "#000000",
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                }}
              >
                {quotedQuestion(current.name || "Untitled test case")}
              </Typography>
            </Box>

            <Box sx={{ mb: 2.5 }}>
              <FieldCaption>Ground Truth</FieldCaption>
              <Box
                sx={{
                  bgcolor: "#F5F5F5",
                  borderRadius: "6px",
                  p: { xs: 1, sm: 1.5 },
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.25,
                  minWidth: 0,
                }}
              >
                <TruthBox
                  label={expectedBehaviorFieldLabels(isContentGenerator).banner}
                  tone="expected"
                  text={unwrapResponseText(current.groundTruth?.expected_output || current.groundTruth?.expected_behavior || current.groundTruth?.pass_criteria || "")}
                />
                <TruthBox label="PROHIBITED" tone="prohibited" text={current.groundTruth?.fail_criteria || ""} />
              </Box>
            </Box>

            <Box>
              <FieldCaption>Test Input / Prompt</FieldCaption>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                {prompts.length ? (
                  prompts.map((text, i) => (
                    <Box
                      key={`${current.id}-prompt-${i}`}
                      sx={{
                        bgcolor: "#F5F5F5",
                        borderRadius: "6px",
                        px: 1.5,
                        py: 1.25,
                      }}
                    >
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#707070", fontFamily: FONT, mb: 0.5 }}>
                        Prompt {i + 1}
                      </Typography>
                      <Typography sx={{ fontSize: 14, color: "#000000", fontFamily: FONT, lineHeight: 1.5, wordBreak: "break-word", overflowWrap: "anywhere" }}>
                        {quotedQuestion(text)}
                      </Typography>
                    </Box>
                  ))
                ) : (
                  <Typography sx={{ fontSize: 13, color: "#98A2B3", fontFamily: FONT }}>No prompts recorded.</Typography>
                )}
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                mt: { xs: 2, md: 3 },
                pt: 2,
                borderTop: "1px solid #E6E6E6",
                minWidth: 0,
              }}
            >
              <Button
                disableElevation
                disableRipple
                disabled={previousDisabled}
                onClick={goPrev}
                startIcon={<ChevronLeftIcon sx={{ fontSize: 18 }} />}
                sx={{ ...navBtnSx, flexShrink: 1, minWidth: 0, px: { xs: "8px", sm: "12px" } }}
              >
                Previous
              </Button>
              <Button
                disableElevation
                disableRipple
                disabled={nextDisabled}
                onClick={goNext}
                endIcon={<ChevronRightIcon sx={{ fontSize: 18 }} />}
                sx={{ ...navBtnSx, flexShrink: 1, minWidth: 0, px: { xs: "8px", sm: "12px" } }}
              >
                Next
              </Button>
            </Box>
          </Box>

          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              flexShrink: 0,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: { xs: "stretch", sm: "flex-end" },
              gap: 1,
              px: { xs: 1.5, sm: 2, md: 3 },
              py: 1,
              bgcolor: "#FFFFFF",
              zIndex: 1,
              borderTop: { xs: "1px solid #E6E6E6", md: "none" },
            }}
          >
            {current.source === "Custom" ? null : (
            <Button
              disableElevation
              disableRipple
              disabled={busy || current.status === "deactivated"}
              onClick={() => onSuspend?.(current)}
              sx={{ ...suspendBtnSx, flex: { xs: "1 1 auto", sm: "0 0 auto" } }}
            >
              Deactivate
            </Button>
            )}
            <Button
              disableElevation
              disableRipple
              disabled={busy}
              onClick={() => setEditOpen(true)}
              sx={{ ...editBtnSx, flex: { xs: "1 1 auto", sm: "0 0 auto" } }}
            >
              Edit
            </Button>
            <Button
              disableElevation
              disableRipple
              disabled={busy}
              onClick={handleConfirm}
              sx={{
                ...confirmBtnSx,
                flex: { xs: "1 1 100%", sm: "0 0 auto" },
                whiteSpace: "nowrap",
              }}
            >
              Confirm Ground Truth
            </Button>
          </Box>
            </>
          )}
        </Box>
      </Box>

      <EditReviewTestCaseDrawer
        open={editOpen && !queueClear}
        testCase={current}
        metrics={metrics}
        risks={risks}
        onClose={() => setEditOpen(false)}
        onConfirm={handleConfirmFromFlyout}
        busy={busy}
        isContentGenerator={isContentGenerator}
      />
    </Box>
  );
}

function ReviewQueueHeader({ onClose }) {
  return (
    <Box
      sx={{
        flexShrink: 0,
        px: { xs: 2, md: 3 },
        py: { xs: 1.25, md: 1.75 },
        bgcolor: "#FFFFFF",
        borderBottom: "1px solid #E6E6E6",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
        <IconButton
          onClick={onClose}
          aria-label="Back to test cases"
          sx={{ color: "#383838", mt: 0.15, ml: -1 }}
        >
          <ArrowBackIcon sx={{ fontSize: 20 }} />
        </IconButton>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: { xs: 18, md: 20 }, fontWeight: 800, color: "#111827", fontFamily: FONT, lineHeight: 1.3 }}>
            Review queue
          </Typography>
          <Typography sx={{ fontSize: { xs: 12, md: 13 }, color: "#667085", mt: 0.35, fontFamily: FONT, lineHeight: 1.45 }}>
            One disposition at a time. Only cases that are new or flagged for review appear here.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
